import { getEngine } from '../../../app/api.js';
import '../../../scene/scene.js';
import { Vector3 } from '@zephyr3d/base';
import '../../../values.js';
import '../../../render/render_queue.js';
import '../../../render/primitive.js';
import '@zephyr3d/device';
import '../../../material/shader/helper.js';
import '../../../material/lambert.js';
import '../../../material/blinn.js';
import '../../../material/unlit.js';
import '../../../material/particle.js';
import '../../../material/subsurfaceprofile.js';
import '../../../material/meshmaterial.js';
import '../../../material/grassmaterial.js';
import '../../../material/terrain-cm.js';
import '../../../material/pbrmr.js';
import '../../../material/pbrsg.js';
import '../../../material/mtoon.js';
import '../../../material/pbrblueprint.js';
import '../../../material/sprite.js';
import '../../../material/sprite_std.js';
import { defineProps } from '../types.js';
import '../../blueprint/material/texture.js';
import '../../../render/rendergraph/forward_plus_builder.js';
import '../../../render/lightpass.js';
import '../../../render/sky.js';
import '../../../render/clipmap.js';
import '../../../shaders/atmosphere.js';
import { GraphNode } from '../../../scene/graph_node.js';
import '../../../shadow/shadowmapper.js';
import '../../../scene/mesh.js';
import '../../../scene/basesprite.js';
import { TextSprite } from '../../../scene/textsprite.js';
import { MSDFText } from '../../../scene/msdftext.js';
import { MSDFTextSprite } from '../../../scene/msdftextsprite.js';
import '../../../scene/water.js';
import '../../../scene/scene_node.js';
import '../../../scene/particlesys.js';
import '../../../scene/batchgroup.js';
import '../../../scene/terrain-cm/terrain-cm.js';
import '../../../scene/terrain-cm/grass.js';
import '../../../scene/raycast_visitor.js';

/** @internal */ function getTextSpriteClass() {
    return {
        ctor: TextSprite,
        name: 'TextSprite',
        parent: GraphNode,
        noTitle: true,
        createFunc (ctx) {
            const node = new TextSprite(ctx.scene);
            node.parent = ctx;
            return {
                obj: node
            };
        },
        getProps () {
            return defineProps([
                {
                    name: 'Anchor',
                    description: 'Sprite pivot in normalized UV space',
                    type: 'vec2',
                    default: [
                        0.5,
                        0.5
                    ],
                    get (value) {
                        value.num[0] = this.anchorX;
                        value.num[1] = this.anchorY;
                    },
                    set (value) {
                        this.anchorX = value.num[0];
                        this.anchorY = value.num[1];
                    }
                },
                {
                    name: 'Resolution',
                    description: 'Text render target resolution',
                    type: 'int2',
                    options: {
                        minValue: 1,
                        maxValue: 4096
                    },
                    default: [
                        128,
                        128
                    ],
                    get (value) {
                        value.num[0] = this.resolutionX;
                        value.num[1] = this.resolutionY;
                    },
                    set (value) {
                        this.resolutionX = value.num[0];
                        this.resolutionY = value.num[1];
                    }
                },
                {
                    name: 'Font',
                    description: 'Canvas font string (e.g. "12px arial")',
                    type: 'string',
                    default: '12px arial',
                    get (value) {
                        value.str[0] = this.font;
                    },
                    set (value) {
                        this.font = value.str[0];
                    }
                },
                {
                    name: 'Text',
                    description: 'Displayed text content',
                    type: 'string',
                    default: '',
                    options: {
                        multiline: true
                    },
                    get (value) {
                        value.str[0] = this.text;
                    },
                    set (value) {
                        this.text = value.str[0];
                    }
                },
                {
                    name: 'TextColor',
                    description: 'Color of the rendered text',
                    type: 'rgb',
                    default: [
                        1,
                        1,
                        1
                    ],
                    get (value) {
                        const c = this.textColor;
                        value.num[0] = c.x;
                        value.num[1] = c.y;
                        value.num[2] = c.z;
                    },
                    set (value) {
                        this.textColor = new Vector3(value.num[0], value.num[1], value.num[2]);
                    }
                }
            ]);
        }
    };
}
/** @internal */ function getMSDFTextSpriteClass() {
    return {
        ctor: MSDFTextSprite,
        name: 'MSDFTextSprite',
        parent: GraphNode,
        noTitle: true,
        createFunc (ctx) {
            const node = new MSDFTextSprite(ctx.scene);
            node.parent = ctx;
            return {
                obj: node
            };
        },
        getProps () {
            return defineProps([
                {
                    name: 'Anchor',
                    description: 'Sprite pivot in normalized layout-box space',
                    type: 'vec2',
                    default: [
                        0.5,
                        0.5
                    ],
                    get (value) {
                        value.num[0] = this.anchorX;
                        value.num[1] = this.anchorY;
                    },
                    set (value) {
                        this.anchorX = value.num[0];
                        this.anchorY = value.num[1];
                    }
                },
                {
                    name: 'FontAsset',
                    description: 'Font asset path used to build the runtime MSDF glyph atlas',
                    type: 'object',
                    default: '',
                    options: {
                        mimeTypes: [
                            'font/ttf',
                            'font/otf'
                        ]
                    },
                    isNullable () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = this.fontAsset ? getEngine().resourceManager.getAssetId(this.fontAsset) ?? '' : '';
                    },
                    async set (value) {
                        this.fontAsset = value.str[0] ? await getEngine().resourceManager.fetchFontAsset(value.str[0]) : null;
                    }
                },
                {
                    name: 'FontSize',
                    description: 'Font size in local-space units before node scaling',
                    type: 'float',
                    default: 32,
                    options: {
                        minValue: 1,
                        maxValue: 4096
                    },
                    get (value) {
                        value.num[0] = this.fontSize;
                    },
                    set (value) {
                        this.fontSize = value.num[0];
                    }
                },
                {
                    name: 'MaxWidth',
                    description: 'Maximum layout width in local-space units, 0 disables wrapping',
                    type: 'float',
                    default: 0,
                    options: {
                        minValue: 0,
                        maxValue: 4096
                    },
                    get (value) {
                        value.num[0] = this.maxWidth;
                    },
                    set (value) {
                        this.maxWidth = value.num[0];
                    }
                },
                {
                    name: 'TextAlign',
                    description: 'Horizontal alignment within the layout width',
                    type: 'string',
                    default: 'left',
                    options: {
                        enum: {
                            labels: [
                                'Left',
                                'Center',
                                'Right'
                            ],
                            values: [
                                'left',
                                'center',
                                'right'
                            ]
                        }
                    },
                    get (value) {
                        value.str[0] = this.textAlign;
                    },
                    set (value) {
                        this.textAlign = value.str[0];
                    }
                },
                {
                    name: 'Text',
                    description: 'Displayed text content',
                    type: 'string',
                    default: '',
                    options: {
                        multiline: true
                    },
                    get (value) {
                        value.str[0] = this.text;
                    },
                    set (value) {
                        this.text = value.str[0];
                    }
                },
                {
                    name: 'TextColor',
                    description: 'Color of the rendered text',
                    type: 'rgb',
                    default: [
                        1,
                        1,
                        1
                    ],
                    get (value) {
                        const c = this.textColor;
                        value.num[0] = c.x;
                        value.num[1] = c.y;
                        value.num[2] = c.z;
                    },
                    set (value) {
                        this.textColor = new Vector3(value.num[0], value.num[1], value.num[2]);
                    }
                },
                {
                    name: 'OutlineColor',
                    description: 'Color of the SDF outline rendered around the text',
                    type: 'rgb',
                    default: [
                        0,
                        0,
                        0
                    ],
                    get (value) {
                        const c = this.outlineColor;
                        value.num[0] = c.x;
                        value.num[1] = c.y;
                        value.num[2] = c.z;
                    },
                    set (value) {
                        this.outlineColor = new Vector3(value.num[0], value.num[1], value.num[2]);
                    }
                },
                {
                    name: 'OutlineWidth',
                    description: 'Outline thickness in text local-space units; it scales with the text when moving closer or farther',
                    type: 'float',
                    default: 0,
                    options: {
                        minValue: 0,
                        maxValue: 4096
                    },
                    get (value) {
                        value.num[0] = this.outlineWidth;
                    },
                    set (value) {
                        this.outlineWidth = value.num[0];
                    }
                }
            ]);
        }
    };
}
/** @internal */ function getMSDFTextClass() {
    return {
        ctor: MSDFText,
        name: 'MSDFText',
        parent: GraphNode,
        noTitle: true,
        createFunc (ctx) {
            const node = new MSDFText(ctx.scene);
            node.parent = ctx;
            return {
                obj: node
            };
        },
        getProps () {
            return defineProps([
                {
                    name: 'Anchor',
                    description: 'Sprite pivot in normalized layout-box space',
                    type: 'vec2',
                    default: [
                        0.5,
                        0.5
                    ],
                    get (value) {
                        value.num[0] = this.anchorX;
                        value.num[1] = this.anchorY;
                    },
                    set (value) {
                        this.anchorX = value.num[0];
                        this.anchorY = value.num[1];
                    }
                },
                {
                    name: 'FontAsset',
                    description: 'Font asset path used to build the runtime MSDF glyph atlas',
                    type: 'object',
                    default: '',
                    options: {
                        mimeTypes: [
                            'font/ttf',
                            'font/otf'
                        ]
                    },
                    isNullable () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = this.fontAsset ? getEngine().resourceManager.getAssetId(this.fontAsset) ?? '' : '';
                    },
                    async set (value) {
                        this.fontAsset = value.str[0] ? await getEngine().resourceManager.fetchFontAsset(value.str[0]) : null;
                    }
                },
                {
                    name: 'FontSize',
                    description: 'Font size in local-space units before node scaling',
                    type: 'float',
                    default: 32,
                    options: {
                        minValue: 1,
                        maxValue: 4096
                    },
                    get (value) {
                        value.num[0] = this.fontSize;
                    },
                    set (value) {
                        this.fontSize = value.num[0];
                    }
                },
                {
                    name: 'MaxWidth',
                    description: 'Maximum layout width in local-space units, 0 disables wrapping',
                    type: 'float',
                    default: 0,
                    options: {
                        minValue: 0,
                        maxValue: 4096
                    },
                    get (value) {
                        value.num[0] = this.maxWidth;
                    },
                    set (value) {
                        this.maxWidth = value.num[0];
                    }
                },
                {
                    name: 'TextAlign',
                    description: 'Horizontal alignment within the layout width',
                    type: 'string',
                    default: 'left',
                    options: {
                        enum: {
                            labels: [
                                'Left',
                                'Center',
                                'Right'
                            ],
                            values: [
                                'left',
                                'center',
                                'right'
                            ]
                        }
                    },
                    get (value) {
                        value.str[0] = this.textAlign;
                    },
                    set (value) {
                        this.textAlign = value.str[0];
                    }
                },
                {
                    name: 'Text',
                    description: 'Displayed text content',
                    type: 'string',
                    default: '',
                    options: {
                        multiline: true
                    },
                    get (value) {
                        value.str[0] = this.text;
                    },
                    set (value) {
                        this.text = value.str[0];
                    }
                },
                {
                    name: 'TextColor',
                    description: 'Color of the rendered text',
                    type: 'rgb',
                    default: [
                        1,
                        1,
                        1
                    ],
                    get (value) {
                        const c = this.textColor;
                        value.num[0] = c.x;
                        value.num[1] = c.y;
                        value.num[2] = c.z;
                    },
                    set (value) {
                        this.textColor = new Vector3(value.num[0], value.num[1], value.num[2]);
                    }
                },
                {
                    name: 'OutlineColor',
                    description: 'Color of the SDF outline rendered around the text',
                    type: 'rgb',
                    default: [
                        0,
                        0,
                        0
                    ],
                    get (value) {
                        const c = this.outlineColor;
                        value.num[0] = c.x;
                        value.num[1] = c.y;
                        value.num[2] = c.z;
                    },
                    set (value) {
                        this.outlineColor = new Vector3(value.num[0], value.num[1], value.num[2]);
                    }
                },
                {
                    name: 'OutlineWidth',
                    description: 'Outline thickness in text local-space units; it scales with the text when moving closer or farther',
                    type: 'float',
                    default: 0,
                    options: {
                        minValue: 0,
                        maxValue: 4096
                    },
                    get (value) {
                        value.num[0] = this.outlineWidth;
                    },
                    set (value) {
                        this.outlineWidth = value.num[0];
                    }
                }
            ]);
        }
    };
}

export { getMSDFTextClass, getMSDFTextSpriteClass, getTextSpriteClass };
//# sourceMappingURL=text.js.map
